// Source of truth for redirect-error codes is Rails Humans::OmniauthCallbacksController
// at https://github.com/opendoor-labs/web/blob/master/app/controllers/humans/omniauth_callbacks_controller.rb
//
// The codes are provider-agnostic — the same controller handles Google and Apple
// redirects — so this module is shared by every redirect-auth button. Only the
// `missing_email` and `identity_mismatch` copy names the provider; pass the
// display name (defaults to "Google") to render the matching wording.

import { AUTH_ERROR_PROVIDER_QUERY_PARAM, AUTH_ERROR_QUERY_PARAM } from '../globals';

// Providers an inline error can be attributed to. Mirrors Rails REDIRECT_FLOW_PROVIDERS.
export const AUTH_ERROR_PROVIDERS = ['google', 'apple'] as const;
export type AuthErrorProvider = (typeof AUTH_ERROR_PROVIDERS)[number];

export const AUTH_ERROR_CODES = [
  'missing_email',
  'provider_error',
  'record_not_found',
  'missing_strategy',
  'reauth_requires_login',
  'identity_mismatch',
  'link_conflict',
  'email_verification_required',
  'unknown',
] as const;

export type OAuthRedirectErrorCode = (typeof AUTH_ERROR_CODES)[number];

// Provider-interpolating copy needs a provider-free form: Rails omits
// `auth_error_provider` on exactly the paths where attribution failed, and
// naming a provider there tells the user a method they may never have pressed
// went wrong ("your Google account" after an Apple attempt).
const buildMessages = (provider?: string): Record<OAuthRedirectErrorCode, string> => ({
  missing_email: provider
    ? `We couldn't get an email from your ${provider} account. Try again or sign in with email.`
    : "We couldn't get an email from that account. Try again or sign in with email.",
  provider_error:
    'This email is already linked to a different sign-in method. Please use your original sign-in method.',
  record_not_found: "We couldn't find or create your account. Please try again or contact support.",
  missing_strategy: "Sign-in isn't available right now. Please try again in a moment.",
  reauth_requires_login: 'Your session has expired. Please sign in again to continue.',
  identity_mismatch: provider
    ? `That ${provider} account doesn't match the one you're signed in as. Please choose the matching account.`
    : "That account doesn't match the one you're signed in as. Please choose the matching account.",
  link_conflict:
    'This email is already registered. Sign in with your original method, then link this account from your settings.',
  email_verification_required: 'This email is already registered. Verify your email to continue.',
  unknown: 'Something went wrong signing you in. Please try again.',
});

const isOAuthRedirectErrorCode = (raw: string): raw is OAuthRedirectErrorCode =>
  (AUTH_ERROR_CODES as readonly string[]).includes(raw);

export const normalizeAuthErrorCode = (raw: string | null | undefined): OAuthRedirectErrorCode => {
  if (raw && isOAuthRedirectErrorCode(raw)) {
    return raw;
  }
  return 'unknown';
};

/** `provider` omitted renders the attribution-free copy — pass it only when the
 *  failure is actually attributed to a provider. */
export const getAuthErrorMessage = (raw: string | null | undefined, provider?: string): string =>
  buildMessages(provider)[normalizeAuthErrorCode(raw)];

export const readRawAuthErrorFromLocation = (): string | null => {
  if (typeof window === 'undefined') return null;
  const raw = new URLSearchParams(window.location.search).get(AUTH_ERROR_QUERY_PARAM);
  return raw && raw.length > 0 ? raw : null;
};

/** True when `?auth_error_provider=` is present and non-empty, valid or not.
 *  Distinguishes an absent param from an unknown one (allowlist drift). SSR-safe. */
export const hasAuthErrorProviderParam = (): boolean => {
  if (typeof window === 'undefined') return false;
  const raw = new URLSearchParams(window.location.search).get(AUTH_ERROR_PROVIDER_QUERY_PARAM);
  return raw !== null && raw.length > 0;
};

/**
 * Reads `?auth_error_provider=`. Returns the lowercased value when it is a known
 * provider, else null (neutral). SSR-safe.
 */
export const readAuthErrorProviderFromLocation = (): AuthErrorProvider | null => {
  if (typeof window === 'undefined') return null;
  const raw = new URLSearchParams(window.location.search)
    .get(AUTH_ERROR_PROVIDER_QUERY_PARAM)
    ?.toLowerCase();
  return raw && (AUTH_ERROR_PROVIDERS as readonly string[]).includes(raw)
    ? (raw as AuthErrorProvider)
    : null;
};

/**
 * Removes `auth_error` and `auth_error_provider` via replaceState, preserving
 * other params, the pathname, and the hash. No-op if neither is present or in SSR.
 */
export const stripAuthErrorFromUrl = (): void => {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  const hadParam =
    url.searchParams.has(AUTH_ERROR_QUERY_PARAM) ||
    url.searchParams.has(AUTH_ERROR_PROVIDER_QUERY_PARAM);
  if (!hadParam) return;
  url.searchParams.delete(AUTH_ERROR_QUERY_PARAM);
  url.searchParams.delete(AUTH_ERROR_PROVIDER_QUERY_PARAM);
  const search = url.searchParams.toString();
  const newUrl = url.pathname + (search ? `?${search}` : '') + (url.hash || '');
  window.history.replaceState({}, '', newUrl);
};
